/**
 * Componente: Payment (Módulo de Pago)
 * Permite realizar pagos con NFC
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNFCContext } from '../contexts/NFCContext';
import { useProductContext } from '../contexts/ProductContext';
import { useTransactionContext } from '../contexts/TransactionContext';
import { useAuthContext } from '../contexts/AuthContext';
import { NFC_STATES, TRANSACTION_TYPES } from '../utils/constants';
import { formatCurrency } from '../utils/formatters';
import { calculateCartTotal, calculateNewBalance } from '../utils/helpers';
import { validatePaymentAmount } from '../utils/validators';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import './Payment.css';

const Payment = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { nfcState, nfcData, startScan, writeNFC, reset } = useNFCContext();
  const { getProductsByVendor } = useProductContext();
  const { createTransaction } = useTransactionContext();

  const [cart, setCart] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);

  const products = getProductsByVendor(user?.id);
  const total = calculateCartTotal(cart);

  const handleScanCard = () => {
    reset();
    setCart([]);
    startScan();
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    
    if (existing) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, cantidad: item.cantidad + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, cantidad: 1 }]);
    }
    
    toast.success(`✅ ${product.nombre} agregado al carrito`, {
      autoClose: 2000,
      position: 'top-right',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, cantidad: newQuantity }
        : item
    ));
  };

  const handleConfirmPayment = async () => {
    if (!nfcData) {
      toast.error('Debes escanear una tarjeta primero');
      return;
    }

    if (cart.length === 0) {
      toast.error('Agrega productos al carrito');
      return;
    }

    // Valida saldo
    const validation = validatePaymentAmount(total, nfcData.saldo);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setProcessing(true);

    try {
      // Calcula nuevo saldo
      const newBalance = calculateNewBalance(nfcData.saldo, total, false);

      // Crea el registro de transacción primero
      const transaction = {
        tipo: TRANSACTION_TYPES.PAGO,
        estudianteId: nfcData.estudianteId,
        estudianteNombre: nfcData.nombre,
        vendedorId: user.id,
        vendedorNombre: user.nombre,
        monto: -total,
        saldoAnterior: nfcData.saldo,
        saldoNuevo: newBalance,
        productos: cart.map(item => ({
          id: item.id,
          nombre: item.nombre,
          precio: item.precio,
          cantidad: item.cantidad
        }))
      };

      const createdTransaction = createTransaction(transaction);

      // Escribe nuevo saldo Y la transacción en la tarjeta NFC
      const writeResult = await writeNFC({
        estudianteId: nfcData.estudianteId,
        saldo: newBalance,
        transaccion: {
          id: createdTransaction.id,
          tipo: createdTransaction.tipo,
          monto: createdTransaction.monto,
          fecha: createdTransaction.fecha,
          vendedorId: user.id,
          productos: transaction.productos
        }
      });

      if (!writeResult.success) {
        throw new Error('Error escribiendo en la tarjeta NFC');
      }

      toast.success(`✅ ¡Pago exitoso! Nuevo saldo: ${formatCurrency(newBalance)}`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Limpia todo
      setCart([]);
      reset();
      setShowCartModal(false);
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Error procesando pago:', error);
      toast.error(error.message || 'Error procesando el pago');
    } finally {
      setProcessing(false);
    }
  };

  // Renderizar productos (usado en desktop y modal)
  const renderProducts = () => (
    <div className="products-grid">
      {products.length === 0 ? (
        <div className="empty-products">
          <div className="empty-icon">📦</div>
          <p>No hay productos disponibles</p>
        </div>
      ) : (
        products.map(product => (
          <div 
            key={product.id} 
            className={`product-card ${!nfcData ? 'disabled' : ''}`}
            onClick={() => nfcData && addToCart(product)}
          >
            <div className="product-image">
              <span className="product-emoji">
                {product.categoria === 'Comida' ? '🍔' :
                 product.categoria === 'Bebida' ? '🥤' :
                 product.categoria === 'Snack' ? '🍿' : '📦'}
              </span>
            </div>
            <div className="product-info">
              <h3 className="product-name">{product.nombre}</h3>
              <p className="product-category">{product.categoria}</p>
            </div>
            <div className="product-footer">
              <span className="product-price">{formatCurrency(product.precio)}</span>
              <button className="add-product-btn">
                <span>+</span>
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  // Renderizar carrito (usado en desktop y modal)
  const renderCart = () => (
    <>
      {cart.length === 0 ? (
        <div className="empty-cart">
          <div className="empty-cart-icon">🛒</div>
          <h3>Carrito vacío</h3>
          <p>Agrega productos para comenzar</p>
        </div>
      ) : (
        <>
          <div className="cart-items">
            {cart.map(item => (
              <div key={item.id} className="cart-item">
                <div className="item-info">
                  <div className="item-icon">
                    {item.categoria === 'Comida' ? '🍔' :
                     item.categoria === 'Bebida' ? '🥤' :
                     item.categoria === 'Snack' ? '🍿' : '📦'}
                  </div>
                  <div className="item-details">
                    <h4 className="item-name">{item.nombre}</h4>
                    <p className="item-price">{formatCurrency(item.precio)} c/u</p>
                  </div>
                </div>
                <div className="item-controls">
                  <div className="quantity-selector">
                    <button 
                      className="qty-btn decrease"
                      onClick={() => updateQuantity(item.id, item.cantidad - 1)}
                    >
                      −
                    </button>
                    <span className="qty-display">{item.cantidad}</span>
                    <button 
                      className="qty-btn increase"
                      onClick={() => updateQuantity(item.id, item.cantidad + 1)}
                    >
                      +
                    </button>
                  </div>
                  <div className="item-total">{formatCurrency(item.precio * item.cantidad)}</div>
                  <button 
                    className="delete-btn"
                    onClick={() => removeFromCart(item.id)}
                    title="Eliminar"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-row subtotal">
              <span className="summary-label">Subtotal ({cart.reduce((acc, item) => acc + item.cantidad, 0)} items)</span>
              <span className="summary-value">{formatCurrency(total)}</span>
            </div>

            {nfcData && (
              <>
                <div className="summary-row balance">
                  <span className="summary-label">
                    <span className="label-icon">💰</span>
                    Saldo actual
                  </span>
                  <span className="summary-value">{formatCurrency(nfcData.saldo)}</span>
                </div>
                
                <div className="summary-divider"></div>
                
                <div className={`summary-row new-balance ${nfcData.saldo - total < 0 ? 'negative' : ''}`}>
                  <span className="summary-label">
                    <span className="label-icon">💳</span>
                    Saldo después del pago
                  </span>
                  <span className="summary-value">{formatCurrency(nfcData.saldo - total)}</span>
                </div>
              </>
            )}

            <div className="summary-divider-thick"></div>

            <div className="summary-row total">
              <span className="summary-label">Total a pagar</span>
              <span className="summary-value total-amount">{formatCurrency(total)}</span>
            </div>
          </div>

          <button
            onClick={handleConfirmPayment}
            disabled={!nfcData || processing || (nfcData && nfcData.saldo < total)}
            className="confirm-payment-btn"
          >
            {processing ? (
              <>
                <div className="btn-spinner"></div>
                <span>Procesando pago...</span>
              </>
            ) : nfcData && nfcData.saldo < total ? (
              <>
                <span className="btn-icon">⚠️</span>
                <span>Saldo insuficiente</span>
              </>
            ) : !nfcData ? (
              <>
                <span className="btn-icon">📱</span>
                <span>Escanea un carnet primero</span>
              </>
            ) : (
              <>
                <span className="btn-icon">✓</span>
                <span>Confirmar y Procesar Pago</span>
              </>
            )}
          </button>
        </>
      )}
    </>
  );

  return (
    <div className="payment-container">
      {/* Header Premium */}
      <header className="payment-header">
        <div className="header-content">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <span className="back-icon">←</span>
            <span>Volver</span>
          </button>
          <div className="header-title">
            <div className="title-icon">💳</div>
            <div>
              <h1>Punto de Venta</h1>
              <p>Procesar pago con NFC</p>
            </div>
          </div>
          <div className="header-badge">
            <span className="badge-icon">🏪</span>
            <span>{user?.nombre}</span>
          </div>
        </div>
      </header>

      <div className="payment-content">
        {/* Panel NFC - Siempre visible */}
        <div className="nfc-panel">
          <div className="panel-header">
            <span className="step-badge">Paso 1</span>
            <h2 className="panel-title">
              <span className="panel-icon">📱</span>
              Escanear Carnet
            </h2>
          </div>
          
          {nfcState === NFC_STATES.IDLE && (
            <div className="scan-area">
              <button onClick={handleScanCard} className="scan-btn">
                <div className="scan-icon-wrapper">
                  <div className="scan-rings">
                    <div className="ring ring-1"></div>
                    <div className="ring ring-2"></div>
                    <div className="ring ring-3"></div>
                  </div>
                  <span className="scan-icon">📱</span>
                </div>
                <span className="scan-text">Iniciar Escaneo NFC</span>
                <span className="scan-hint">Toca para activar el lector</span>
              </button>
            </div>
          )}

          {nfcState === NFC_STATES.SCANNING && (
            <div className="scanning-state">
              <div className="scanning-animation">
                <div className="pulse-rings">
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring"></div>
                </div>
                <div className="nfc-icon-animated">📱</div>
              </div>
              <h3>Buscando dispositivo...</h3>
              <p>Acerca el carnet al lector NFC</p>
              <div className="progress-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          {nfcState === NFC_STATES.SUCCESS && nfcData && (
            <div className="student-card">
              <div className="card-header">
                <div className="student-avatar">
                  {nfcData.nombre.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div className="check-badge">✓</div>
              </div>
              <div className="card-body">
                <h3 className="student-name">{nfcData.nombre}</h3>
                <div className="student-details">
                  <div className="detail-item">
                    <span className="detail-icon">🎓</span>
                    <span className="detail-label">Grado:</span>
                    <span className="detail-value">{nfcData.grado}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">💰</span>
                    <span className="detail-label">Saldo disponible:</span>
                    <span className="detail-value balance-amount">{formatCurrency(nfcData.saldo)}</span>
                  </div>
                </div>
              </div>
              <button onClick={handleScanCard} className="rescan-btn">
                <span>🔄</span> Escanear otro carnet
              </button>
            </div>
          )}

          {nfcState === NFC_STATES.ERROR && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h3>Error de lectura</h3>
              <p>No se pudo leer el carnet. Intenta nuevamente.</p>
              <button onClick={handleScanCard} className="retry-btn">
                Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Panel Productos - Solo desktop */}
        <div className="products-panel desktop-only">
          {renderProducts()}
        </div>

        {/* Panel Carrito - Solo desktop */}
        <div className="cart-panel desktop-only">
          {renderCart()}
        </div>
      </div>

      {/* Floating Action Buttons - Solo móvil */}
      {nfcData && (
        <div className="mobile-fab-container">
          <button
            className="fab fab-products"
            onClick={() => setShowProductsModal(true)}
            title="Ver productos"
          >
            <span className="fab-icon">🛒</span>
            <span className="fab-label">Productos</span>
          </button>

          <button
            className="fab fab-cart"
            onClick={() => setShowCartModal(true)}
            title="Ver carrito"
          >
            <span className="fab-icon">🛍️</span>
            {cart.length > 0 && (
              <span className="fab-badge">{cart.length}</span>
            )}
            <span className="fab-label">Carrito</span>
          </button>
        </div>
      )}

      {/* Modal de Productos - Móvil */}
      <Modal
        isOpen={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        title="Seleccionar Productos"
        size="large"
      >
        {renderProducts()}
      </Modal>

      {/* Modal de Carrito - Móvil */}
      <Modal
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        title={`Carrito ${cart.length > 0 ? `(${cart.length})` : ''}`}
        size="medium"
      >
        {renderCart()}
      </Modal>
    </div>
  );
};

export default Payment;
